import { useEffect, useState } from 'react';
import { getAuthToken } from '../commom/getAuthToken';
import env from '../env';

export type StampedPdfError =
  | 'unauthorized' // 401 - sem token / guest
  | 'forbidden' // 403 - sem permissão (conteúdo pago)
  | 'not-found' // 404 - material não encontrado
  | 'generic'; // demais falhas de rede/servidor

interface UseStampedPdfParams {
  /**
   * ID do material, usado no endpoint carimbado. O backend resolve por `_id`
   * (materiais vinculados a cursos podem ter `display: false` e não são
   * encontrados por slug no catálogo).
   */
  materialId?: string;
  /** URL crua do storage, usada como fallback para materiais não-PDF (ex.: áudio → 422). */
  fallbackUrl?: string;
  /** Só busca o PDF carimbado quando habilitado (ex.: material é PDF). */
  enabled?: boolean;
}

interface UseStampedPdfResult {
  /** URL pronta para o iframe/link: um blob: do PDF carimbado ou o fallback. */
  url: string | null;
  isLoading: boolean;
  error: StampedPdfError | null;
}

function mapStatusToError(status: number): StampedPdfError {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  return 'generic';
}

/**
 * Busca o PDF do material já carimbado (marca d'água por aluno/acesso) no endpoint
 * autenticado `GET /materials/{materialId}/stamped-pdf`, com o token no header, e devolve
 * um `blob:` URL para uso no iframe e no botão "Abrir em nova guia".
 *
 * Materiais não-PDF (endpoint responde 422) caem no `fallbackUrl` (uri original).
 */
export function useStampedPdf({
  materialId,
  fallbackUrl,
  enabled = true,
}: UseStampedPdfParams): UseStampedPdfResult {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<StampedPdfError | null>(null);

  useEffect(() => {
    // Não é PDF (ou sem id): usa a uri original, sem carimbar.
    if (!enabled || !materialId) {
      setUrl(fallbackUrl ?? null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        if (!token) {
          if (!cancelled) setError('unauthorized');
          return;
        }

        const res = await fetch(
          `${env.REACT_APP_API_URL}/materials/${materialId}/stamped-pdf`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Material não é PDF → volta para a uri original (sem marca).
        if (res.status === 422) {
          if (!cancelled) setUrl(fallbackUrl ?? null);
          return;
        }

        if (!res.ok) {
          if (!cancelled) setError(mapStatusToError(res.status));
          return;
        }

        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setUrl(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch {
        if (!cancelled) setError('generic');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [materialId, fallbackUrl, enabled]);

  return { url, isLoading, error };
}
